import React from "react";
import Text from "@src/lib/ui/Text";
import useUser from "@src/features/app/hooks/useUser";
import { isInPast } from "@src/lib/utils/time";
import useBranding from "@src/features/settings/hooks/useBranding";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import { cn } from "@src/lib/utils/cn";
import CheckmarkCurvedIcon from "@src/lib/icons/12px/checkmark-curved";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import GlobeIcon from "@src/lib/icons/18px/globe";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import { useOrgSwitcherQuery } from "@src/features/app/top-nav/OrgSwitcher.generated";
import { OrgLogo } from "@src/features/app/top-nav/OrgLogo";
import useSwitchOrgAndReload from "@src/features/app/hooks/useSwitchOrgAndReload";

type Props = {
  triggerClassName?: string;
  railItem?: boolean;
};

type OrgSwitcherCommandProps = {
  onSelectOrg?: () => void;
};

export const OrgSwitcherCommand: React.FC<OrgSwitcherCommandProps> = ({
  onSelectOrg,
}) => {
  const { user } = useUser();
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });
  const { data, loading, previousData } = useOrgSwitcherQuery({
    variables: {
      input: {
        search:
          debouncedSearch.length > 0 ? { value: debouncedSearch } : undefined,
      },
    },
  });
  const { switchOrgAndReload } = useSwitchOrgAndReload();
  const availableOrgs = React.useMemo(
    () =>
      data?.Me?.availableOrgs.objects ||
      previousData?.Me?.availableOrgs.objects ||
      [],
    [data, previousData],
  );
  const selectOrg = React.useCallback(
    (orgId: number) => {
      const orgName = availableOrgs.find((org) => org.id === orgId)?.name;
      if (!orgName) return;
      switchOrgAndReload(orgId, orgName);
      onSelectOrg?.();
    },
    [availableOrgs, onSelectOrg, switchOrgAndReload],
  );

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search organizations..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {loading ? (
          <CommandItem disabled>Loading...</CommandItem>
        ) : availableOrgs.length === 0 ? (
          <CommandItem disabled>No organizations found</CommandItem>
        ) : (
          availableOrgs.map((org) => (
            <CommandItem
              key={org.id}
              onSelect={() => selectOrg(org.id)}
              className="flex items-center gap-2 py-2"
            >
              <OrgLogo
                orgId={org.id}
                logoUrl={
                  org.brandDetails?.logoUrls?.thumb ||
                  org.brandDetails?.logoUrls?.original
                }
                orgName={org.name}
              />
              <Text
                variant="compact-description"
                className={cn(
                  "flex-1 truncate",
                  org.deactivatedAt &&
                    isInPast(org.deactivatedAt) &&
                    "text-gray-7",
                )}
              >
                {org.name}
              </Text>
              {org.id === user?.org?.id && (
                <CheckmarkCurvedIcon className="text-gray-9" />
              )}
            </CommandItem>
          ))
        )}
      </CommandList>
    </Command>
  );
};

const LeftNavOrgSwitcher: React.FC<Props> = ({
  triggerClassName,
  railItem,
}) => {
  const { user } = useUser();
  const [open, setOpen] = React.useState(false);
  const { data, loading, previousData } = useOrgSwitcherQuery({
    variables: {
      input: {},
    },
  });
  const totalCount = React.useMemo(
    () =>
      data?.Me?.totalOrgCount.totalCount ||
      previousData?.Me?.totalOrgCount.totalCount ||
      0,
    [data, previousData],
  );
  const disabled = React.useMemo(
    () => totalCount <= 1 && !loading,
    [totalCount, loading],
  );
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "o" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);
  const { name, brandDetails } = useBranding();

  const trigger = (
    <SidebarMenuButton
      size="default"
      className={cn(
        "relative gap-1.5 pl-1.5 pr-2 !transition-none",
        triggerClassName,
      )}
      tooltip={name || "Organization"}
      aria-label={`Organization: ${name || "Unknown"}. Switch organization.`}
    >
      {railItem ? (
        <>
          <GlobeIcon />
          <span className="truncate">Org</span>
        </>
      ) : (
        <>
          <OrgLogo
            orgId={user?.org?.id || 0}
            size={24}
            logoUrl={
              brandDetails?.logoUrls?.thumb || brandDetails?.logoUrls?.original
            }
            orgName={name || ""}
            className="size-6 max-w-none rounded-md"
            useClassSize
          />
          <span className="truncate text-s-v4 font-medium-v4 text-foreground group-data-[collapsible=icon]:sr-only">
            {name}
          </span>
          {!disabled && (
            <ChevronDownIcon className="ml-auto !size-3 opacity-50 group-data-[collapsible=icon]:hidden" />
          )}
        </>
      )}
    </SidebarMenuButton>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {disabled ? (
          trigger
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent
              className="w-[300px] p-0"
              align={railItem ? "end" : "start"}
              side={railItem ? "right" : "bottom"}
              sideOffset={6}
            >
              <OrgSwitcherCommand onSelectOrg={() => setOpen(false)} />
            </PopoverContent>
          </Popover>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default LeftNavOrgSwitcher;
